import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  CreditCard, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  Crown, 
  Sparkles,
  Calendar,
  IndianRupee,
  History,
  Ban,
  Check,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Indian timezone utility
const formatDateIndian = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  });
};

const formatDateTimeIndian = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });
};

function Payments() {
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [offers, setOffers] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, historyRes, statusRes] = await Promise.all([
        axios.get(`${API}/api/subscription/plans`),
        axios.get(`${API}/api/payments/history`),
        axios.get(`${API}/api/subscription/status`)
      ]);

      setPlans(plansRes.data.plans || []);
      setOffers(plansRes.data.active_offers || []);
      setSubscriptionData({
        ...statusRes.data,
        current: plansRes.data.current_subscription
      });
      setPaymentHistory(historyRes.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      toast.error('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setPaymentDialog(true);
  };

  const calculateFinalPrice = (plan) => {
    if (!plan) return 0;
    let price = plan.price;
    
    // Check for applicable offers
    const applicableOffer = offers.find(o => 
      o.is_active && 
      (!o.applicable_plans || o.applicable_plans.length === 0 || o.applicable_plans.includes(plan.id))
    );
    
    if (applicableOffer) {
      if (applicableOffer.discount_type === 'percentage') {
        price = price - (price * applicableOffer.discount_value / 100);
      } else {
        price = price - applicableOffer.discount_value;
      }
    }
    
    return Math.max(0, price);
  };

  const handlePayment = async () => {
    if (!selectedPlan) return;
    
    setProcessingPayment(true);
    
    try {
      const finalPrice = calculateFinalPrice(selectedPlan);
      
      // Create order
      const orderRes = await axios.post(`${API}/api/payments/create-order`, {
        plan_id: selectedPlan.id,
        plan_name: selectedPlan.name,
        amount: finalPrice
      });
      
      const { order_id, amount, currency, key_id } = orderRes.data;
      
      // Initialize Razorpay
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'Fuelogic',
        description: `${selectedPlan.name} Subscription`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API}/api/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            toast.success('Payment successful! Subscription activated.');
            setPaymentDialog(false);
            fetchData();
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: subscriptionData?.current?.pump_name || '',
          email: subscriptionData?.current?.email || '',
          contact: subscriptionData?.current?.mobile || ''
        },
        theme: {
          color: '#f59e0b'
        },
        modal: {
          ondismiss: function () {
            setProcessingPayment(false);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getStatusBadge = () => {
    if (!subscriptionData) return null;
    
    if (subscriptionData.is_suspended) {
      return (
        <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium flex items-center gap-1">
          <Ban className="w-4 h-4" />
          Suspended
        </span>
      );
    }
    if (subscriptionData.is_expired) {
      return (
        <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-medium flex items-center gap-1">
          <AlertTriangle className="w-4 h-4" />
          Expired {subscriptionData.is_in_grace_period && '(Grace Period)'}
        </span>
      );
    }
    if (subscriptionData.days_remaining <= 7) {
      return (
        <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-sm font-medium flex items-center gap-1">
          <Clock className="w-4 h-4" />
          Expiring Soon
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium flex items-center gap-1">
        <CheckCircle className="w-4 h-4" />
        Active
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        {/* Page Header */}
        <div className="page-header mb-8">
          <div>
            <h1 className="page-title">Subscription & Payments</h1>
            <p className="page-subtitle">Manage your subscription plan</p>
          </div>
          {getStatusBadge()}
        </div>

        {/* Suspension Warning */}
        {subscriptionData?.is_suspended && (
          <Card className="p-5 bg-red-50 border-red-200 mb-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Ban className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-red-800">Account Suspended</h3>
                <p className="text-red-700 text-sm mt-1">
                  Your subscription has expired and your account is suspended. You have limited access to the application.
                  Please renew your subscription to restore full access.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Current Subscription Status */}
        <Card className="content-card mb-6" data-testid="subscription-status-card">
          <div className="content-card-header">
            <h2 className="content-card-title">
              <Crown className="w-5 h-5 text-amber-500" />
              Current Subscription
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="stat-card-label mb-1">Plan</p>
              <p className="font-heading font-bold text-xl capitalize">
                {subscriptionData?.current?.subscription_level || 'Basic'}
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="stat-card-label mb-1">Valid Until</p>
              <p className="font-heading font-bold text-xl">
                {formatDateIndian(subscriptionData?.subscription_end)}
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="stat-card-label mb-1">Days Remaining</p>
              <p className={`font-heading font-bold text-xl ${
                subscriptionData?.days_remaining <= 0 ? 'text-red-600' :
                subscriptionData?.days_remaining <= 7 ? 'text-amber-600' :
                'text-emerald-600'
              }`}>
                {subscriptionData?.days_remaining <= 0 
                  ? 'Expired' 
                  : `${subscriptionData?.days_remaining} days`}
              </p>
            </div>
          </div>
        </Card>

        {/* Active Offers */}
        {offers.length > 0 && (
          <Card className="p-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Special Offers
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {offers.map((offer) => (
                <div key={offer.id} className="p-4 bg-white rounded-lg border border-amber-200">
                  <h3 className="font-semibold text-amber-800">{offer.name}</h3>
                  <p className="text-sm text-amber-700 mt-1">{offer.description}</p>
                  <p className="text-lg font-bold text-amber-600 mt-2">
                    {offer.discount_type === 'percentage' 
                      ? `${offer.discount_value}% OFF` 
                      : `₹${offer.discount_value} OFF`}
                  </p>
                  {offer.valid_until && (
                    <p className="text-xs text-amber-500 mt-1">
                      Valid until: {formatDateIndian(offer.valid_until)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Subscription Plans */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Available Plans
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const finalPrice = calculateFinalPrice(plan);
              const hasDiscount = finalPrice < plan.price;
              
              return (
                <Card 
                  key={plan.id} 
                  className={`p-6 relative ${plan.is_popular ? 'border-2 border-amber-400 shadow-lg' : ''}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.is_popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold mt-2">{plan.name}</h3>
                  <p className="text-sm text-muted mt-1">{plan.description}</p>
                  
                  <div className="my-4">
                    {hasDiscount && (
                      <p className="text-sm text-muted line-through">₹{plan.price.toLocaleString('en-IN')}</p>
                    )}
                    <p className="text-3xl font-bold">
                      ₹{finalPrice.toLocaleString('en-IN')}
                      <span className="text-sm font-normal text-muted">
                        /{plan.duration_months === 1 ? 'month' : `${plan.duration_months} months`}
                      </span>
                    </p>
                    {hasDiscount && (
                      <p className="text-sm text-green-600 font-medium">
                        Save ₹{(plan.price - finalPrice).toLocaleString('en-IN')}!
                      </p>
                    )}
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className="w-full"
                    variant={plan.is_popular ? 'default' : 'outline'}
                    onClick={() => handleSelectPlan(plan)}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    Select Plan
                  </Button>
                </Card>
              );
            })}
          </div>
          
          {plans.length === 0 && (
            <Card className="p-8 text-center">
              <p className="text-muted">No subscription plans available at the moment.</p>
              <p className="text-sm text-muted mt-2">Please contact support for assistance.</p>
            </Card>
          )}
        </div>

        {/* Payment History */}
        <Card className="p-6" data-testid="payment-history-card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Payment History
          </h2>
          
          {paymentHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Plan</th>
                    <th className="text-right py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Payment ID</th>
                    <th className="text-left py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.map((payment) => (
                    <tr key={payment.id} className="border-b hover:bg-secondary/50">
                      <td className="py-3 px-4">{formatDateTimeIndian(payment.paid_at)}</td>
                      <td className="py-3 px-4">{payment.plan_name}</td>
                      <td className="py-3 px-4 text-right font-medium">
                        ₹{payment.amount?.toLocaleString('en-IN')}
                      </td>
                      <td className="py-3 px-4 text-xs font-mono text-muted">
                        {payment.razorpay_payment_id || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          payment.status === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {payment.status?.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-muted py-8">No payment history found.</p>
          )}
        </Card>

        {/* Payment Confirmation Dialog */}
        <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Subscription</DialogTitle>
              <DialogDescription>
                Review your selected plan before proceeding to payment.
              </DialogDescription>
            </DialogHeader>
            
            {selectedPlan && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-secondary rounded-lg">
                  <h3 className="font-semibold text-lg">{selectedPlan.name}</h3>
                  <p className="text-sm text-muted">{selectedPlan.description}</p>
                  
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span>Plan Price</span>
                      <span>₹{selectedPlan.price?.toLocaleString('en-IN')}</span>
                    </div>
                    {calculateFinalPrice(selectedPlan) < selectedPlan.price && (
                      <div className="flex justify-between items-center text-green-600">
                        <span>Discount</span>
                        <span>-₹{(selectedPlan.price - calculateFinalPrice(selectedPlan)).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center font-bold text-lg mt-2 pt-2 border-t">
                      <span>Total</span>
                      <span>₹{calculateFinalPrice(selectedPlan).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-muted">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Duration: {selectedPlan.duration_months === 1 
                      ? '1 month' 
                      : `${selectedPlan.duration_months} months`}
                  </span>
                </div>
              </div>
            )}
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPaymentDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePayment} 
                disabled={processingPayment}
                className="bg-gradient-to-r from-amber-500 to-orange-500"
                data-testid="confirm-payment-btn"
              >
                {processingPayment ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Processing...
                  </>
                ) : (
                  <>
                    <IndianRupee className="w-4 h-4 mr-2" />
                    Pay Now
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default Payments;
